import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import * as LucideIcons from "lucide-react";
import { Check, HelpCircle, Trophy, type LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/game/Panel";
import { StatBar } from "@/components/game/StatBar";
import { Pill } from "@/components/game/Badges";
import {
  FilterPills,
  GoldAmount,
  MicroLabel,
  PageHeader,
  ScreenContainer,
} from "@/components/game/ScreenKit";
import { listMyAchievements, claimAchievement } from "@/lib/game/achievements.functions";
import {
  achievementTitle,
  achievementDescription,
  type AchievementCategory,
} from "@/lib/game/achievements";
import { RouteError, RouteNotFound } from "@/components/game/RouteError";
import { PageSkeleton } from "@/components/game/PageSkeleton";
import { useTutorial } from "@/components/game/TutorialProvider";
import { tServerError } from "@/lib/i18n/server-error";

export const Route = createFileRoute("/_app/game/achievements")({
  component: AchievementsPage,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

const CATEGORIES: readonly AchievementCategory[] = [
  "combat",
  "collection",
  "breeding",
  "exploration",
  "meta",
];

/**
 * Der Katalog trägt den lucide-Namen als freien String (`icon: "Swords"`),
 * die Auflösung passiert deshalb zur Laufzeit. Ein einziger Cast an dieser
 * Stelle statt `any` an jeder Kachel — das Namensraum-Objekt enthält neben den
 * Icons auch Helfer, die hier nie nachgeschlagen werden.
 */
const ICON_BY_NAME = LucideIcons as unknown as Record<string, LucideIcon | undefined>;

/**
 * Zustand einer Erfolgs-Kachel. Die Farbe der Icon-Kachel trägt ihn:
 * grau = noch offen, gold = Belohnung wartet, grün = abgeholt.
 */
type AchState = "locked" | "claimable" | "claimed";

const TILE_BG: Record<AchState, string> = {
  locked: "var(--secondary)",
  claimable: "var(--gold)",
  claimed: "var(--hp-ally)",
};

const TILE_FG: Record<AchState, string> = {
  locked: "var(--muted-foreground)",
  claimable: "var(--ink)",
  claimed: "var(--ink)",
};

function AchievementsPage() {
  useTutorial("achievements_intro");
  const { t } = useTranslation("game");
  const fetchAch = useServerFn(listMyAchievements);
  const claimFn = useServerFn(claimAchievement);
  const qc = useQueryClient();
  const [tab, setTab] = useState<AchievementCategory | "all">("all");

  const { data, isLoading } = useQuery({
    queryKey: ["achievements"],
    queryFn: () => fetchAch(),
  });

  const claim = useMutation({
    mutationFn: (id: string) => claimFn({ data: { id } }),
    onSuccess: (res) => {
      toast.success(t("achievements.toastClaimed", { gold: res.reward_gold }));
      qc.invalidateQueries({ queryKey: ["achievements"] });
      qc.invalidateQueries({ queryKey: ["my-monster"] });
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  if (isLoading || !data) return <PageSkeleton />;

  const unlocked = data.filter((a) => a.unlocked_at).length;
  const total = data.length;
  const claimable = data.filter((a) => a.unlocked_at && !a.claimed_at).length;

  const filtered = tab === "all" ? data : data.filter((a) => a.def.category === tab);

  const filterItems: ReadonlyArray<readonly [AchievementCategory | "all", string]> = [
    ["all", t("achievements.tabAll")],
    ...CATEGORIES.map((c) => [c, t(`achievements.category.${c}`)] as const),
  ];

  return (
    <ScreenContainer>
      <PageHeader
        icon={<Trophy className="h-[22px] w-[22px] shrink-0 text-primary" strokeWidth={2.5} />}
        title={t("achievements.title")}
        sub={t("achievements.subtitle", { unlocked, total, claimable })}
      />

      {/* Kategorie-Filter über den Baustein: Er trägt `aria-pressed` selbst
          (die Zustands-Ansage, für die hier einmal nachgebaut wurde) und die
          44-px-Staffelung fürs Handy. */}
      <FilterPills items={filterItems} value={tab} onChange={setTab} />

      <div className="mt-3.5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((a) => {
          const locked = !a.unlocked_at;
          const hidden = a.def.hidden && locked;
          const state: AchState = a.claimed_at ? "claimed" : locked ? "locked" : "claimable";
          const IconC = hidden ? HelpCircle : (ICON_BY_NAME[a.def.icon] ?? Trophy);
          const showClaim = !!a.unlocked_at && !a.claimed_at;

          return (
            /* Bewusst ohne `tone`: Der Panel-Ton ist semantisch (Gegner, Elite,
               Boss) und nie dekorativ — ein einlösbarer Erfolg ist kein Boss.
               Dazu ist `--boss` derselbe Farbwert wie `--gold` (styles.css),
               eine „goldene Umrandung" wäre hier also von einem echten
               Boss-Panel nicht zu unterscheiden, und der Boss-Ton tauscht als
               einziger auch die Fläche. Den Zustand tragen die farbige
               Icon-Kachel (grau/gold/grün) und der Gold-Knopf. */
            <Panel key={a.def.id} className={hidden ? "opacity-70" : undefined}>
              <div className="flex items-start gap-2.5">
                <div
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-md border-2 border-ink"
                  style={{ background: TILE_BG[state], color: TILE_FG[state] }}
                >
                  <IconC className="h-5 w-5" strokeWidth={2.5} />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <MicroLabel className="pt-0.5">
                      {t(`achievements.category.${a.def.category}`)}
                    </MicroLabel>
                    {/* Die Belohnung ist Gold — es gibt keine zweite Währung. */}
                    <span className="shrink-0">
                      <GoldAmount value={a.def.reward_gold} />
                      <span className="sr-only"> {t("achievements.goldShort")}</span>
                    </span>
                  </div>

                  <h3 className="mt-0.5 text-[15px] leading-tight [overflow-wrap:anywhere]">
                    {hidden ? "???" : achievementTitle(a.def)}
                  </h3>

                  <p className="mt-1 text-xs leading-snug text-muted-foreground [overflow-wrap:anywhere]">
                    {hidden ? t("achievements.hiddenHint") : achievementDescription(a.def)}
                  </p>

                  <StatBar
                    className="mt-2.5"
                    label=""
                    value={Math.min(a.progress, a.def.goal)}
                    max={a.def.goal}
                    color="xp"
                  />

                  {showClaim || a.claimed_at ? (
                    <div className="mt-2.5 flex items-center">
                      {/* Die Größe `sm` allein wäre auf dem Handy nur 40 px hoch
                          und ab 640 px sogar 32 px. Das ist die einzige echte
                          Aktion dieser Seite — sie bleibt am Daumen ≥ 44 px,
                          genau wie die Knöpfe im Stall. */}
                      {showClaim ? (
                        <Button
                          size="sm"
                          variant="gold"
                          onClick={() => claim.mutate(a.def.id)}
                          disabled={claim.isPending}
                          className="min-h-11 sm:min-h-9"
                        >
                          {t("achievements.claimBtn")}
                        </Button>
                      ) : null}
                      {a.claimed_at ? (
                        <Pill style={{ background: "var(--hp-ally)", color: "var(--ink)" }}>
                          <Check className="h-3 w-3" strokeWidth={2.5} />
                          {t("achievements.claimed")}
                        </Pill>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </Panel>
          );
        })}
      </div>
    </ScreenContainer>
  );
}
